import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MapIcon from "@mui/icons-material/Map";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import PlaceIcon from "@mui/icons-material/Place";
import StoresTable from "./StoresTable";
import GlobalButton from "../../../../shared/utils/GlobalButton";

/**
 * Props
 *   deliveryCenter    {object}
 *   isSelected        {boolean}           Map focus
 *   onSelectDC        {(id) => void}
 *   isDragging        {boolean}           Something is being dragged anywhere on the page
 *   isDragOver        {boolean}           The current drag is hovering over THIS accordion
 *   onDragOver        {(e) => void}       Pass through to drop zone
 *   onDragLeave       {() => void}
 *   onDrop            {(e) => void}
 *   onStoreDragStart  {(e, store) => void}  Start dragging a mapped store OUT
 *   onStoreDragEnd    {() => void}
 *   onMoveToUnmapped  {(store) => void}   Kebab "Move to Unmapped"
 */
const DeliveryCenterAccordion = ({
  deliveryCenter,
  isSelected,
  onSelectDC,
  isDragging,
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onStoreDragStart,
  onStoreDragEnd,
  onMoveToUnmapped,
  distance = 10, // global radius from the single Batch Parameters slider
}) => {
  const [expanded, setExpanded] = useState(false);

  const hasCoordinates = Boolean(
    deliveryCenter.coordinates?.latitude &&
    deliveryCenter.coordinates?.longitude,
  );

  const handleMapClick = (e) => {
    e.stopPropagation();
    onSelectDC(isSelected ? null : deliveryCenter._id);
  };

  // ── Border / background logic ─────────────────────────────────────────────
  // Priority: drag-over (green) > map-selected (blue) > default
  const borderColor = isDragOver
    ? "#2E7D32"
    : isSelected
      ? "#1565C0"
      : "#E5E7EB";
  const borderWidth = isDragOver || isSelected ? 2 : 1;
  const headerBg = isDragOver ? "#F1F8F1" : isSelected ? "#EEF4FF" : "#FAFAFA";

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded((prev) => !prev)}
      elevation={0}
      // ── This is the drop zone ──────────────────────────────────────────────
      // We attach drag events to the whole accordion so the user can drop on
      // either the header or the expanded store list.
      onDragOver={isDragging ? onDragOver : undefined}
      onDragLeave={isDragging ? onDragLeave : undefined}
      onDrop={isDragging ? onDrop : undefined}
      sx={{
        mb: 2,
        border: `${borderWidth}px solid ${borderColor}`,
        borderRadius: "12px !important",
        overflow: "hidden",
        transition: "border 0.15s, background 0.15s",
        // Lift shadow when a store is being dragged over
        boxShadow: isDragOver ? "0 4px 20px rgba(46,125,50,0.18)" : "none",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          bgcolor: headerBg,
          minHeight: 70,
          transition: "background 0.15s",
        }}
      >
        <Box
          width="100%"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pr={1}
        >
          {/* LEFT */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                bgcolor: isDragOver
                  ? "#2E7D32"
                  : isSelected
                    ? "#1565C0"
                    : "#E8F0FE",
                color: isDragOver || isSelected ? "#fff" : "#1565C0",
                transition: "background 0.15s",
              }}
            >
              <WarehouseIcon />
            </Avatar>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={700} fontSize={16}>
                  {deliveryCenter.cityName} Delivery Center
                </Typography>
                {/* Drop hint badge */}
                {isDragOver && (
                  <Chip
                    label="Drop to assign ↓"
                    size="small"
                    color="success"
                    sx={{ fontWeight: 700, fontSize: 11 }}
                  />
                )}
                {/* Prompt only visible while dragging (not over) */}
                {isDragging && !isDragOver && (
                  <Typography variant="caption" color="text.disabled">
                    ← drag here
                  </Typography>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <PlaceIcon sx={{ fontSize: 15, color: "gray" }} />
                <Typography variant="body2" color="text.secondary">
                  {deliveryCenter.cityName}, {deliveryCenter.stateName}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          {/* RIGHT */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              color="primary"
              size="small"
              label={`${deliveryCenter.stores.length} Stores`}
            />

            {/* Radius chip — reads the global slider value */}
            <Chip
              size="small"
              variant="outlined"
              color="secondary"
              label={`⬤ ${distance} km radius`}
              sx={{ fontWeight: 600, fontSize: 11 }}
            />

            <Chip
              variant="outlined"
              size="small"
              label={deliveryCenter.batchTitle}
              sx={{ fontWeight: 700 }}
            />

            <Tooltip
              title={
                hasCoordinates
                  ? isSelected
                    ? "Clear map focus"
                    : "View this DC on map"
                  : "No coordinates stored for this DC"
              }
            >
              <span>
                <GlobalButton
                  size="small"
                  variant={isSelected ? "primary" : "secondary"}
                  leftIcon={<MapIcon />}
                  disabled={!hasCoordinates}
                  onClick={handleMapClick}
                >
                  {isSelected ? "On Map ✓" : "View"}
                </GlobalButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>
      </AccordionSummary>

      <Divider />

      <AccordionDetails sx={{ p: 0 }}>
        <StoresTable
          deliveryCenter={deliveryCenter}
          onStoreDragStart={onStoreDragStart}
          onStoreDragEnd={onStoreDragEnd}
          onMoveToUnmapped={onMoveToUnmapped}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default DeliveryCenterAccordion;
